import { Thermometer, FileText, Clock, DollarSign, Pencil, Brain, Sparkles, Globe, Code, Search, Image, Monitor, Link } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { ModelTierInfo, AgentInfo, BuiltInToolSettings } from '@/types';

interface PresetCardProps {
  tierKey: string;
  tier: ModelTierInfo;
  agents?: AgentInfo[];
  onEdit: (tierKey: string) => void;
}

// 프리셋 색상 매핑
const presetColors: Record<string, string> = {
  // Anthropic
  'claude-opus': 'bg-purple-500',
  'claude-sonnet': 'bg-blue-500',
  'claude-haiku': 'bg-green-500',
  // OpenAI
  'gpt-pro': 'bg-emerald-600',
  'gpt-standard': 'bg-emerald-500',
  'gpt-chat': 'bg-teal-500',
  'gpt-mini': 'bg-cyan-500',
  // Google Gemini
  'gemini-pro': 'bg-red-500',
  'gemini-flash': 'bg-orange-500',
};

// 프리셋 라벨
const presetLabels: Record<string, string> = {
  'claude-opus': 'Claude Opus',
  'claude-sonnet': 'Claude Sonnet',
  'claude-haiku': 'Claude Haiku',
  'gpt-pro': 'GPT Pro',
  'gpt-standard': 'GPT Standard',
  'gpt-chat': 'GPT Chat',
  'gpt-mini': 'GPT Mini',
  'gemini-pro': 'Gemini Pro',
  'gemini-flash': 'Gemini Flash',
};

// 비용 등급 색상
const costTierStyles: Record<string, string> = {
  high: 'bg-red-100 text-red-700 border-red-200',
  medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  low: 'bg-green-100 text-green-700 border-green-200',
};

const costTierLabels: Record<string, string> = {
  high: '높음',
  medium: '보통',
  low: '낮음',
};

// 내장 도구 아이콘 및 라벨
const toolIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  web_search: Globe,
  google_search: Search,
  file_search: Search,
  code_interpreter: Code,
  code_execution: Code,
  image_generation: Image,
  computer_use: Monitor,
  url_context: Link,
};

const toolLabels: Record<string, string> = {
  web_search: '웹 검색',
  google_search: 'Google 검색',
  file_search: '파일 검색',
  code_interpreter: '코드 실행',
  code_execution: '코드 실행',
  image_generation: '이미지 생성',
  computer_use: '컴퓨터 제어',
  url_context: 'URL 컨텍스트',
};

export default function PresetCard({ tierKey, tier, agents = [], onEdit }: PresetCardProps) {
  const color = presetColors[tier.tier] || 'bg-gray-500';
  const label = presetLabels[tier.tier] || tier.tier;

  // 이 프리셋을 사용하는 에이전트 찾기
  const usingAgents = agents.filter(
    (agent) => agent.modelTier === tier.tier || agent.modelTier === tierKey
  );

  // 파라미터 가져오기
  const params = tier.parameters || tier.defaultParameters;
  const temperature = params?.temperature;
  const maxTokens = params?.max_tokens;
  const timeout = params?.timeout;

  // Reasoning/Thinking 파라미터
  const reasoningEffort = params?.reasoning_effort;
  const thinkingBudgetTokens = params?.thinking_budget_tokens;
  const thinkingLevel = params?.thinking_level;

  // 활성화된 내장 도구 가져오기
  const builtinTools = tier.builtinTools || tier.defaultBuiltinTools || {};
  const enabledTools = Object.entries(builtinTools)
    .filter(([, settings]) => (settings as BuiltInToolSettings)?.enabled)
    .map(([name]) => name);

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Badge className={`${color} text-white`}>{label}</Badge>
            <span className="text-sm text-muted-foreground capitalize">{tier.provider}</span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => onEdit(tierKey)} className="h-8 w-8 p-0">
            <Pencil className="h-4 w-4" />
          </Button>
        </div>
        <CardTitle className="text-sm font-mono">{tier.model}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* 설명 */}
        <p className="text-xs text-muted-foreground">
          {tier.descriptionKo || tier.description}
        </p>

        {/* 파라미터 정보 */}
        <div className="flex flex-wrap items-center gap-2 text-xs">
          {temperature !== undefined && temperature !== null && (
            <div className="flex items-center gap-1 px-2 py-1 bg-muted rounded-md">
              <Thermometer className="h-3 w-3 text-orange-500" />
              <span>{temperature}</span>
            </div>
          )}
          {maxTokens !== undefined && (
            <div className="flex items-center gap-1 px-2 py-1 bg-muted rounded-md">
              <FileText className="h-3 w-3 text-blue-500" />
              <span>{maxTokens.toLocaleString()}</span>
            </div>
          )}
          {timeout !== undefined && (
            <div className="flex items-center gap-1 px-2 py-1 bg-muted rounded-md">
              <Clock className="h-3 w-3 text-gray-500" />
              <span>{timeout}s</span>
            </div>
          )}
          {tier.costTier && (
            <Badge variant="outline" className={`${costTierStyles[tier.costTier] || ''}`}>
              <DollarSign className="h-3 w-3 mr-1" />
              {costTierLabels[tier.costTier] || tier.costTier}
            </Badge>
          )}
        </div>

        {/* Reasoning/Thinking 파라미터 */}
        {(reasoningEffort || thinkingBudgetTokens || thinkingLevel) && (
          <div className="flex flex-wrap items-center gap-2 text-xs">
            {reasoningEffort && reasoningEffort !== 'none' && (
              <div className="flex items-center gap-1 px-2 py-1 bg-purple-100 rounded-md">
                <Brain className="h-3 w-3 text-purple-500" />
                <span className="text-purple-700">추론: {reasoningEffort}</span>
              </div>
            )}
            {thinkingBudgetTokens && (
              <div className="flex items-center gap-1 px-2 py-1 bg-amber-100 rounded-md">
                <Sparkles className="h-3 w-3 text-amber-500" />
                <span className="text-amber-700">사고: {thinkingBudgetTokens.toLocaleString()}tk</span>
              </div>
            )}
            {thinkingLevel && (
              <div className="flex items-center gap-1 px-2 py-1 bg-blue-100 rounded-md">
                <Brain className="h-3 w-3 text-blue-500" />
                <span className="text-blue-700">사고: {thinkingLevel}</span>
              </div>
            )}
          </div>
        )}

        {/* 활성화된 내장 도구 */}
        {enabledTools.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 text-xs">
            {enabledTools.map((toolName) => {
              const ToolIcon = toolIcons[toolName] || Globe;
              const toolLabel = toolLabels[toolName] || toolName;
              return (
                <div key={toolName} className="flex items-center gap-1 px-2 py-1 bg-cyan-100 rounded-md">
                  <ToolIcon className="h-3 w-3 text-cyan-600" />
                  <span className="text-cyan-700">{toolLabel}</span>
                </div>
              );
            })}
          </div>
        )}

        {/* 사용하는 에이전트 */}
        {usingAgents.length > 0 && (
          <div className="pt-2 border-t">
            <div className="text-xs text-muted-foreground mb-1">사용하는 에이전트</div>
            <div className="flex flex-wrap gap-1">
              {usingAgents.slice(0, 3).map((agent) => (
                <Badge key={agent.type} variant="secondary" className="text-xs">
                  {(agent.nameKo || agent.name).replace(' Agent', '')}
                </Badge>
              ))}
              {usingAgents.length > 3 && (
                <Badge variant="secondary" className="text-xs">
                  +{usingAgents.length - 3}
                </Badge>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
